import z from "zod";
import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";

import { zodParseWithLog } from "src/api/apiErrorLogs";
import { Scores, ScoresSchema } from "src/api/client-api/post/scoreSchema";
import { mrtSortingStateToObject, SortType } from "src/api/apiHelpers";

type Options = {
    cacheTime?: number;
    page?: number;
    itemsPerPage?: number;
    search?: string;
    companyId?: number;
    sorting?: Array<SortType>;
};

export const packagingGridKey = "allPackagingsGrid" as const;

export const useGetAllPackaging = (options?: Options) => {
    const { cacheTime = 0, page = 1, itemsPerPage = 20, search = "", sorting = [], companyId } = options || {};

    const query = useQuery({
        queryKey: [packagingGridKey, page, itemsPerPage, search, companyId, sorting],
        staleTime: 0,
        cacheTime: cacheTime,
        queryFn: async () => {
            const url = "/api/packagings";

            return await loggedInApiCall.get(url, {
                params: {
                    itemsPerPage: itemsPerPage,
                    page: page,
                    q: search !== "" ? search : undefined,
                    company_id: companyId,
                    order: mrtSortingStateToObject(sorting),
                },
            });
        },
        select: (request) => {
            const totalItems = request.data["hydra:totalItems"];
            const packaging = request.data["hydra:member"];
            const data = zodParseWithLog(packaging, PackagingListSchema.array(), "allPackagings");
            return {
                items: data,
                totalItems: totalItems,
            };
        },
    });

    return {
        packagings: query.data?.items,
        totalItems: query.data?.totalItems,
        ...query,
    };
};

export type PackagingList = {
    "@id": IRI;
    id: number;
    name: string;
    externalId?: string;
    canView: boolean;
    canModify: boolean;
    canDelete: boolean;
    scores: Scores;
};

const PackagingListSchema: z.ZodSchema<PackagingList> = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
    externalId: z.string().optional(),
    canView: z.boolean(),
    canModify: z.boolean(),
    canDelete: z.boolean(),
    scores: ScoresSchema,
});
